/**
 * SpeedyRC Hero runtime (storefront)
 *
 * Theme delivery (normal): the `flux-hero` snippet contains the built hero plus upcoming scheduled states as
 *   <template data-srh-from="ISO">, and an inline script that swaps to the right state before paint (device clock).
 *   This file then: checks the device clock against Shopify's own `Date` header (trusted time), swaps if needed,
 *   swaps again exactly when the next state starts, and enhances the visible hero.
 * mount(container, { apiUrl })  JS fallback: fetch the live hero, render it (SpeedyRCHeroRender), then enhance
 * enhance(root, { apiUrl, track, now }) live prices + stock, countdowns, swipe dots, impression/click tracking
 *
 * Everything degrades: if a fetch fails the rendered snapshot stays as-is; no Axis needed for the swaps.
 */
(function () {
    'use strict';

    const FLUSH_DELAY_MS = 8000;
    const CLOCK_TOLERANCE_MS = 60 * 1000;
    const MAX_TIMEOUT_MS = 2147483647;

    function api(apiUrl, path) {
        return `${String(apiUrl || '').replace(/\/$/, '')}${path}`;
    }

    function money(value) {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? `$${n.toFixed(2)}` : '';
    }

    async function hydratePrices(root, apiUrl) {
        const skus = [...new Set([...root.querySelectorAll('[data-srh-sku], [data-srh-stock]')]
            .map(el => el.getAttribute('data-srh-sku') || el.getAttribute('data-srh-stock')).filter(Boolean))];
        if (!skus.length) return;
        try {
            const res = await fetch(api(apiUrl, `/api/public/hero/products?skus=${encodeURIComponent(skus.join(','))}`));
            const json = await res.json();
            const products = (json && json.success && json.data && json.data.products) || {};
            root.querySelectorAll('[data-srh-sku]').forEach(el => {
                const p = products[el.getAttribute('data-srh-sku')];
                if (!p) return;
                const now = el.querySelector('.srh-price__now');
                const was = el.querySelector('.srh-price__was');
                if (now && money(p.price)) now.textContent = money(p.price);
                if (was) {
                    const onSale = Number(p.compareAtPrice) > Number(p.price);
                    was.textContent = onSale ? money(p.compareAtPrice) : '';
                    was.hidden = !onSale;
                }
            });
            root.querySelectorAll('[data-srh-stock]').forEach(el => {
                const p = products[el.getAttribute('data-srh-stock')];
                if (!p) return;
                el.toggleAttribute('data-out', !p.inStock);
                el.textContent = p.inStock ? 'In stock' : (p.preorder ? 'Pre-order' : 'Out of stock');
            });
        } catch (error) {
            // Snapshot prices stay
        }
    }

    function duration(ms) {
        const s = Math.floor(ms / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        const pad = n => String(n).padStart(2, '0');
        return `${d > 0 ? d + 'd ' : ''}${pad(h)}:${pad(m)}:${pad(sec)}`;
    }

    /** Before start → "Starts in", then "Ends in" until the end, then "Ended" (or nothing without an end). */
    function countdownText(start, end, now) {
        if (start && now < start) return `Starts in ${duration(start - now)}`;
        if (end && now < end) return `Ends in ${duration(end - now)}`;
        return end ? 'Ended' : '';
    }

    function startCountdowns(root, now) {
        const els = [...root.querySelectorAll('.srh-countdown')];
        if (!els.length) return null;
        const tick = () => els.forEach(el => {
            const start = Date.parse(el.getAttribute('data-srh-countdown-start') || '') || 0;
            const end = Date.parse(el.getAttribute('data-srh-countdown') || '') || 0;
            el.textContent = countdownText(start, end, now());
        });
        tick();
        return setInterval(tick, 1000);
    }

    function addDots(root) {
        root.querySelectorAll('.srh-row').forEach(row => {
            const cards = row.children.length;
            if (cards < 2 || (row.nextElementSibling && row.nextElementSibling.classList.contains('srh-dots'))) return;
            const dots = document.createElement('div');
            dots.className = 'srh-dots';
            dots.setAttribute('aria-hidden', 'true');
            for (let i = 0; i < cards; i++) dots.appendChild(document.createElement('span'));
            row.after(dots);
            const update = () => {
                const first = row.children[0];
                const step = first ? first.getBoundingClientRect().width + 10 : 1;
                const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
                const index = atEnd ? cards - 1 : Math.round(row.scrollLeft / step);
                [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === index));
            };
            row.addEventListener('scroll', update, { passive: true });
            update();
        });
    }

    function viewOf(el) {
        const view = el.closest('[data-srh-view]');
        return view ? view.getAttribute('data-srh-view') : 'desktop';
    }

    function startTracking(root, apiUrl) {
        const publishId = Number(root.getAttribute('data-srh-publish'));
        if (!publishId || !navigator.sendBeacon) return;
        const url = api(apiUrl, '/api/public/hero/track');
        const seen = new Set();
        let pending = [];
        let timer = null;

        const send = (impressions, clicks) => {
            if (!impressions.length && !clicks.length) return;
            const body = JSON.stringify({ publishId, impressions, clicks });
            navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
        };
        const flush = () => {
            clearTimeout(timer);
            timer = null;
            const batch = pending;
            pending = [];
            send(batch, []);
        };

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;
                    const el = entry.target;
                    const key = `${el.getAttribute('data-srh-tile')}|${viewOf(el)}`;
                    observer.unobserve(el);
                    if (seen.has(key)) return;
                    seen.add(key);
                    pending.push({ tile: el.getAttribute('data-srh-tile'), view: viewOf(el) });
                    if (!timer) timer = setTimeout(flush, FLUSH_DELAY_MS);
                });
            }, { threshold: 0.5 });
            root.querySelectorAll('[data-srh-tile]').forEach(el => observer.observe(el));
        }

        root.addEventListener('click', e => {
            const link = e.target.closest('a[href]');
            const tile = e.target.closest('[data-srh-tile]');
            if (!link || !tile || !root.contains(tile)) return;
            const batch = pending;
            pending = [];
            send(batch, [{ tile: tile.getAttribute('data-srh-tile'), view: viewOf(tile) }]);
        });

        const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', flush);
    }

    /** Returns a cleanup function (stops the countdown timer) for when the hero is swapped out. */
    function enhance(root, options) {
        const opts = options || {};
        if (!root) return () => {};
        hydratePrices(root, opts.apiUrl);
        const timer = startCountdowns(root, opts.now || Date.now);
        addDots(root);
        if (opts.track !== false) startTracking(root, opts.apiUrl);
        return () => clearInterval(timer);
    }

    /**
     * Shopify's server clock via a tiny no-cache HEAD to the store itself (works when Axis is down).
     * Resolves to the offset (ms) to add to Date.now(), or 0 if it can't be read.
     */
    async function trustedOffset() {
        try {
            const sent = Date.now();
            const res = await fetch(`/robots.txt?srh=${sent}`, { method: 'HEAD', cache: 'no-store', credentials: 'omit' });
            const server = Date.parse(res.headers.get('date') || '');
            if (!server) return 0;
            const received = Date.now();
            // Date header has 1s resolution: aim for the middle of that second, and the middle of the round trip
            return server + 500 - (sent + received) / 2;
        } catch (error) {
            return 0;
        }
    }

    /** Theme-built hosts: keep the visible state right for the (trusted) time, now and when the next state starts. */
    function initHost(host) {
        if (host.srhStarted) return;
        host.srhStarted = true;
        const apiUrl = host.getAttribute('data-srh-api') || '';
        let offset = 0;
        let cleanup = () => {};
        let timer = null;
        const now = () => Date.now() + offset;

        // Inline snippet script defines srhApply; fall back to an equivalent if it didn't run
        const apply = typeof host.srhApply === 'function' ? host.srhApply : (at) => {
            let pick = null;
            const templates = [...host.querySelectorAll('template[data-srh-from]')];
            templates.forEach(t => { if (Date.parse(t.getAttribute('data-srh-from')) <= at) pick = t; });
            if (!pick) return false;
            const current = host.querySelector('.srh');
            const fresh = pick.content.cloneNode(true);
            if (current) current.replaceWith(fresh); else host.prepend(fresh);
            for (const t of templates) { t.remove(); if (t === pick) break; }
            return true;
        };

        const start = () => {
            cleanup();
            cleanup = enhance(host.querySelector('.srh'), { apiUrl, now });
            schedule();
        };
        const schedule = () => {
            clearTimeout(timer);
            const next = [...host.querySelectorAll('template[data-srh-from]')]
                .map(t => Date.parse(t.getAttribute('data-srh-from'))).filter(Boolean).sort((a, b) => a - b)[0];
            if (!next) return;
            timer = setTimeout(() => (apply(now()) ? start() : schedule()), Math.min(Math.max(0, next - now()) + 50, MAX_TIMEOUT_MS));
        };

        start();
        trustedOffset().then(o => {
            if (Math.abs(o) < CLOCK_TOLERANCE_MS) return;
            offset = o; // device clock is wrong — use Shopify's
            if (apply(now())) start(); else schedule();
        });
    }

    function initHosts() {
        document.querySelectorAll('.srh-host').forEach(initHost);
    }

    async function mount(container, options) {
        const opts = options || {};
        if (!container || !window.SpeedyRCHeroRender) return;
        try {
            const res = await fetch(api(opts.apiUrl, '/api/public/hero/active'));
            const json = await res.json();
            const data = json && json.success ? json.data : null;
            if (!data || !data.hero) return; // nothing published — theme fallback stays
            container.innerHTML = window.SpeedyRCHeroRender.render(data.hero, {
                publishId: data.publishId,
                versionId: data.versionId,
                campaign: data.versionName
            });
            container.setAttribute('data-srh-mounted', '');
            enhance(container.querySelector('.srh'), opts);
        } catch (error) {
            console.error('[SpeedyRC Hero] Failed to load hero:', error);
        }
    }

    window.SpeedyRCHeroRuntime = { mount, enhance, countdownText, initHosts };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHosts);
    else initHosts();
})();
